import { DownOutlined } from '@ant-design/icons';
import { Dropdown, Menu, Row, message } from 'antd';
import { useState } from 'react';

import {
  Button,
  Form,
  FormItemDatePicker,
  Modal,
  ScreenLayout,
  TabPane,
  Tabs,
} from '../../../components/index';
import {
  ReportsQueryParamsType,
  useLazyExportWebsiteQuery,
  useLazyExportBIQuery,
  useLazyExportBIV2Query,
  useLazyExportPortDockingCargoReportQuery,
  useLazyExportEvasionReportQuery,
} from '../../../services/stopoverApi';
import { createDateString, downloadFile } from '../../../utils/utils';
import { BreakdownsSection } from './breakdownsSection/BreakdownsSection';
import { DockingsSection } from './dockingsSection/DockingsSection';
import { EnvironmentSection } from './environmentSection/EnvironmentSection';
import { OperationsSection } from './operationsSection/OperationsSection';
import { StopoverPendencySection } from './stopoverPendencySection/StopoverPendencySection';
import { SuppressionSection } from './suppressionSection/SuppressionSection';
import { VesselsSection } from './vesselsSection/VesselsSection';
import { WarehousingVehiclesSection } from './warehousingVehiclesSection/WarehousingVehiclesSection';
import { ChargeSection } from './chargeReport/chargeSection';
import { OmissionSection } from './omissionReport/omissionSection';

export type TablePageProps = {
  dataSource: Record<string, any>[];
  scrollOptions?: Record<string, any>;
  total?: number;
  itemsPerPage?: number;
  isLoading?: boolean;
  pageSize?: number;
  showSizeChanger?: boolean;
  rowKey?: string;
  onChangePagination?: (page: number) => number;
  queryPage: number;
};

export function Reports() {
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<{
    key: string;
    label: string;
  } | null>(null);

  function onClickItem(key: any) {
    setSelectedItem(items?.find((item) => item?.key === key.key) || null);
    setIsVisibleModal(true);
  }

  const items = [
    {
      key: 'bi-v2',
      label: 'Levantamento BI',
      onClick: (key: any) => {
        onClickItem(key);
      },
    },
    {
      key: 'port',
      label: 'Relatório porto, cargas e atracações',
      onClick: (key: any) => {
        onClickItem(key);
      },
    },
    {
      key: 'evasion',
      label: 'Relatório de evasão',
      onClick: (key: any) => {
        onClickItem(key);
      },
    },
  ];

  const [form] = Form.useForm();

  const [exportWebsite] = useLazyExportWebsiteQuery();
  const [exportBI] = useLazyExportBIQuery();
  const [exportBIV2] = useLazyExportBIV2Query();
  const [exportPort] = useLazyExportPortDockingCargoReportQuery();
  const [exportEvasion] = useLazyExportEvasionReportQuery();

  async function onGetReport() {
    const dates: ReportsQueryParamsType = {
      initial_date: null,
      final_date: null,
    };
    form.validateFields().then(async (values) => {
      if (
        values &&
        values.range &&
        values.range.length > 0 &&
        values.range[0]
      ) {
        dates.initial_date = createDateString(values.range[0], true);
      }
      if (
        values &&
        values.range &&
        values.range.length > 1 &&
        values.range[1]
      ) {
        dates.final_date = createDateString(values.range[1], true);
      }
      if (selectedItem?.key === 'website') {
        await exportWebsite(dates).then((response) => {
          handleDownload(response, 'website.xlsx');
        });
      }
      if (selectedItem?.key === 'bi') {
        try {
          await exportBI(dates).unwrap();
          setIsVisibleModal(false);
          message.success(
            'Relatório em processamento. Você o receberá em seu e-mail em breve.',
            5
          );
        } catch (error) {
          message.error(
            'Erro ao solicitar a geração do relatório. Tente novamente.'
          );
        }
      }
      if (selectedItem?.key === 'bi-v2') {
        try {
          await exportBIV2(dates).unwrap();
          setIsVisibleModal(false);
          message.success(
            'Relatório em processamento. Você o receberá em seu e-mail em breve.',
            5
          );
        } catch (error) {
          message.error(
            'Erro ao solicitar a geração do relatório. Tente novamente.'
          );
        }
      }
      if (selectedItem?.key === 'port') {
        try {
          await exportPort(dates).unwrap();
          setIsVisibleModal(false);
          message.success(
            'Relatório em processamento. Você o receberá em seu e-mail em breve.',
            5
          );
        } catch (error) {
          message.error(
            'Erro ao solicitar a geração do relatório. Tente novamente.'
          );
        }
      }
      if (selectedItem?.key === 'evasion') {
        try {
          await exportEvasion(dates).unwrap();
          setIsVisibleModal(false);
          message.success(
            'Relatório em processamento. Você o receberá em seu e-mail em breve.',
            5
          );
        } catch (error) {
          message.error(
            'Erro ao solicitar a geração do relatório. Tente novamente.'
          );
        }
      }
    });
  }

  function handleDownload(response: any, fileName: string) {
    if ('data' in response) {
      downloadFile(response.data, fileName);
      setIsVisibleModal(false);
    }
  }

  return (
    <ScreenLayout
      title="Relatórios"
      extra={
        <Dropdown
          placement="bottomRight"
          arrow
          overlay={<Menu items={items} />}
        >
          <Button style={{ marginRight: '10px' }}>
            Baixar relatório gerencial
            <DownOutlined />
          </Button>
        </Dropdown>
      }
    >
      <Modal
        className="TOSPrimaryModal"
        title="Baixar relatório gerencial"
        visible={isVisibleModal}
        okButtonProps={{ htmlType: 'submit', form: 'select_range' }}
        onCancel={() => {
          setIsVisibleModal(false);
          setSelectedItem(null);
        }}
        okText="Baixar"
      >
        <Form
          layout="vertical"
          form={form}
          name="select_range"
          id="select_range"
          onFinish={onGetReport}
        >
          <Row>
            Preencha o período abaixo para baixar o relatório &#34;
            {selectedItem?.label}&#34;:
            <FormItemDatePicker
              label="Período da desatracação"
              name="range"
              isRangePicker
              style={{ marginTop: '10px' }}
              format="DD/MM/YYYY"
            />
          </Row>
        </Form>
      </Modal>
      <Tabs
        defaultActiveKey=""
        size="small"
        tabPosition="top"
        nobackground="true"
      >
        <TabPane tab="Atracação" key="dockings">
          <DockingsSection />
        </TabPane>
        <TabPane tab="Supressão" key="suppression">
          <SuppressionSection />
        </TabPane>
        <TabPane tab="Meio ambiente" key="environment">
          <EnvironmentSection />
        </TabPane>
        <TabPane tab="Operações" key="operations">
          <OperationsSection />
        </TabPane>
        <TabPane tab="Avarias e ocorrências" key="occurences">
          <BreakdownsSection />
        </TabPane>
        <TabPane tab="Movimentação de embarcações" key="movement_of_vessels">
          <VesselsSection />
        </TabPane>
        <TabPane tab="Armazenamento de veículos" key="warehousing_vehicles">
          <WarehousingVehiclesSection />
        </TabPane>
        <TabPane tab="Performance de faturamento" key="charge_report">
          <ChargeSection />
        </TabPane>
        <TabPane tab="Taxa de Omissão" key="omission_report">
          <OmissionSection />
        </TabPane>
        <TabPane tab="Pendências de Escalas" key="stopover_pendencies">
          <StopoverPendencySection />
        </TabPane>
      </Tabs>
    </ScreenLayout>
  );
}
